/**
 * Client-side image compression for chat attachments.
 *
 * Images are stored inline as base64 inside the conversation JSONB blob, so
 * raw screenshots bloat the chat DB. This downscales (longest edge capped)
 * and always re-encodes to WebP before the image is attached. Runs for both
 * pasted and paperclip-uploaded images.
 *
 * Animated GIFs collapse to their first frame (a canvas re-encode is a single
 * frame) — acceptable for screenshots, which is the primary use case.
 */

// Output is always WebP — the format is intentionally not configurable so the
// `.webp` extension and MIME type can never diverge.
const OUTPUT_TYPE = 'image/webp';

export interface CompressOptions {
  /** Cap for the longest edge in px. Larger images are scaled down. */
  maxEdge?: number;
  /** WebP quality, 0..1. */
  quality?: number;
}

/**
 * Returns a compressed `.webp` File. On any failure — decode error, no 2D
 * context, or a result no smaller than the original — returns the original
 * file unchanged so the user's image is never lost.
 */
export async function compressImage(
  file: File,
  { maxEdge = 1568, quality = 0.85 }: CompressOptions = {},
): Promise<File> {
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    const longest = Math.max(width, height);
    const scale = longest > maxEdge ? maxEdge / longest : 1;
    const targetW = Math.max(1, Math.round(width * scale));
    const targetH = Math.max(1, Math.round(height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob((b) => resolve(b), OUTPUT_TYPE, quality),
    );
    if (!blob || blob.size >= file.size) return file;

    const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
    return new File([blob], `${baseName}.webp`, {
      type: OUTPUT_TYPE,
      lastModified: file.lastModified,
    });
  } catch {
    return file;
  }
}
